import React, { useCallback, useEffect, useState } from "react";
import { ContactForm as ContactFormModel } from "@models/MLTC_R3/Contact.model";
import Button from "../Button";
import { InputGroup } from "@components/_Revamp/InputGroup";
import { TitleAndDescription } from "@components/_Revamp/TitleAndDescription/TitleAndDescription";
import { styled } from "@linaria/react";
import useForm from "@utils/useForm";
import { ITheme, useTheme } from "src/theme-revamp";
import { validate } from "./service";
import { useAvivaDispatch, useAvivaSelector } from "@redux/hook";
import { isEmpty } from "lodash";
import { updateContact } from "@redux/slices/MLTC_R3/contactSlice";
import { SubmitSuccess } from "./components/SubmitSuccess";
import { setLoadingState } from "@redux/slices/loadingSlice";
import { Helpers } from "@utils/helpers";
import { postContactShield } from "@api/index";
import { GAAviva } from "@utils/gaAvivaR3";
import Link from "../Link";
import { useHistory } from "react-router-dom";
import Contactus from "@components/_Shared/Contactus";
import { SHIELD_STARTER_HELTH_PLUS } from "@static/staticConstents";
import MainContainer from "../Container";
import { AA as AAShieldStarter } from "@utils/adobeAnalyticsSS";
import { trackOnLoad as trackOnLoadAA } from "@pages/ShieldStarter/Helpers/dataAnalyticsHelpers";

const SubmitNote = styled.p<{ theme: ITheme }>`
  color: ${(props) => props.theme.colors.grey[1400]};
  font-family: 'Open Sans';
  font-size: 14px;
  font-weight: 500;
  line-height: 25px;
  letter-spacing: 0em;
  text-align: left;
  padding: 0 1px;
  span {
    color: ${(props) => props.theme.colors.primary};
    font-weight: 500;
  }
  a {
    font-size: 14px;
    font-weight: 600;
  }
`;

const FormActionContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  column-gap: 24px;
  margin-bottom: 36px;
`;

const ContactInputContainer = styled.div`
  background-color: #fff;
  display: flex;
  flex-direction: column;
  row-gap: 48px;
  margin-bottom: 40px;
  padding: 24px;
  border-radius: 24px;
`;

export const ContactForm = () => {
  const theme = useTheme();
  const history = useHistory();
  const dispatch = useAvivaDispatch();

  const personalInfo = useAvivaSelector(
    (state) => state.shieldStarterStep3.personalInfo
  );
  const selectedPolicy = useAvivaSelector(
    (state) => state.shieldStarterStep2.policyName
  );
  const transactionId = useAvivaSelector(
    (state) => state.shieldStarterStep3.transactionId
  );
  const isRentalPostalCustomer = useAvivaSelector(
    (state) => state.shieldStarterStep3.rentalPostalCustomer
  );
  const agentCode = useAvivaSelector(state=>state.urlAnalytic?.param?.arc) || null
  const campaignCode = useAvivaSelector(state=>state.loading?.campaignDetail?.campaignCode) || null
  const dob = useAvivaSelector(state=>state.shieldStarterStep3.personalInfo?.dateOfBirth)  || null


  const [aaTracking, setAATracking] = useState<string[]>([]);
  const [aaTrackingStart, setAATrackingStart] = useState<string[]>([]);
  const [formSubmited, setFormSubmited] = useState<boolean>(false);

  const { values, errors, patchValue, handleChange, formValid } = useForm(
    new ContactFormModel(personalInfo),
    () => {},
    validate
  );

  const getTransactionId = () => {
    const now = new Date();
    //@ts-ignore
    if (window?.crypto?.randomUUID) {
      //@ts-ignore
      return window.crypto.randomUUID() || `${now.getTime()}`;
    } else {
      return `${now.getTime()}`;
    }
  };

  // const trackOnload = (formName: string): void => {
  //   if (!aaTracking.includes(formName)) {
  //     setAATracking([...aaTracking, formName]);

  //     const GA = GAAviva.getInstance();
  //     let { form, acquisition } = GA.digitalData;
  //     form.name = formName;
  //     GA.trackPageLoad({ form, acquisition });
  //   }
  // };
  // May be added in future requirement
  // const trackOnStart = (formName: string): void => {
  //   if (!aaTrackingStart.includes(formName)) {
  //     setAATrackingStart([...aaTrackingStart, formName]);
  //     const GA = GAAviva.getInstance();
  //     let { form } = GA.digitalData;
  //     form.name = formName;
  //     GA.trackPageStart();
  //   }
  // };
  // May be added in future requirement
  // const trackCTA = () => {
  //   const GA = GAAviva.getInstance();
  //   let { cta } = GA.digitalData;
  //   cta = {
  //     type: "Shield Contact Form",
  //     text: "Submit",
  //   };
  //   GA.trackCTAClick({ cta });
  // };

  const trackFormSubmit = (isError: string): void => {
    const AA = AAShieldStarter.getInstance();
    const { form } = AA.digitalData;
    form.name = "sss_contactus_form_submitted";
    AA.trackFormCompleted({ form }, isError);
  };

  const onInputChange = (e: any) => {
    // trackOnStart("shield_contact_need_help_form");
    handleChange(e);
  };

  const onSubmit = useCallback(async () => {
    dispatch(setLoadingState(true));
    dispatch(updateContact(values));
    const transId = transactionId || getTransactionId();

    const payload = {
      transactionId: transId,
      fullName: values.fullname,
      salutation: values.salutation,
      mobileNumber: values.mobileNum,
      email: values.emailId,
      dateOfBirth:dob,
      product: selectedPolicy || SHIELD_STARTER_HELTH_PLUS,
      rentalPostalCustomer: isRentalPostalCustomer,
      agentCode,
      dropPoint: "QNB_SLSS_CONTACT_US",
      cc: campaignCode
    };

    try {
      const response = await postContactShield(payload);
      trackFormSubmit("not_error");
      Helpers.clearCached();
      dispatch(setLoadingState(false));
      setFormSubmited(true);
    } catch (error) {
      trackFormSubmit("error");
      dispatch(setLoadingState(false));
    }
  }, [transactionId, personalInfo, isRentalPostalCustomer, values]);

  const getSalutionList = useCallback(() => {
    const maleTitles = [
      { id: "Mr", label: "Mr" },
      { id: "Master", label: "Master" },
    ];

    const femaleTitles = [
      { id: "Mrs", label: "Mrs" },
      { id: "Ms", label: "Ms" },
      { id: "Madam", label: "Madam" },
    ];

    const commonTitles = [{ id: "Dr", label: "Dr" }];

    if (personalInfo.gender?.toLowerCase() === "male") {
      return [...maleTitles, ...commonTitles];
    }

    if (personalInfo.gender?.toLowerCase() === "female") {
      return [...femaleTitles, ...commonTitles];
    }

    return [...maleTitles, ...femaleTitles, ...commonTitles];
  }, [personalInfo]);

  useEffect(() => {
    if (personalInfo) {
      patchValue({
        fullname: personalInfo?.fullName,
        mobileNum: personalInfo?.mobileNum,
        emailId: personalInfo?.emailId,
        salutation: personalInfo?.salutation,
      });
    }
  }, [personalInfo]);

  //Track page on page load
  useEffect(()=>{
    trackOnLoadAA("sss_contact_form")
  },[])

  const noBackState: any = history.location.state;
  const isErrorPage: any = noBackState?.noBackButton;

  return !formSubmited ? (
    <>
      <MainContainer
        backButtonFunction={
          isErrorPage
            ? null
            : () => {
                history.goBack();
              }
        }
      >
        <TitleAndDescription
          title={`We're here to help`}
          description={`Fill in your contact details and we'll be in touch to address your queries.`}
          margin="48px 0 64px 0"
        />
        <ContactInputContainer>
          <InputGroup
            labelColor="#434343"
            id="salutation"
            name="salutation"
            label="Salutation"
            inputType="dropdown"
            onChange={onInputChange}
            placeholder="Select"
            dropdownProps={{
              value: values["salutation"],
              source: getSalutionList(),
            }}
          />
          <InputGroup
            labelColor="#434343"
            id="fullname"
            name="fullname"
            label="Full Name"
            placeholder=""
            onChange={(e: any) => {
              const formatted_name = e.target.value.replace(/[^a-z\s]/gi, "");
              onInputChange({
                target: { value: formatted_name, name: e.target.name },
              });
            }}
            invalid={!isEmpty(errors?.fullname?.message)}
            errorMessage={errors?.fullname?.message}
            inputProps={{
              textAlign: "left",
              value: values["fullname"],
            }}
            inputType={"input"}
          />
          <InputGroup
            labelColor="#434343"
            id="mobileNum"
            name="mobileNum"
            label="Mobile Number"
            placeholder=""
            onChange={(e: any) => {
              const regex = /^\d+$/;
              const value = e.target.value;
              if (regex.test(value) && value.length < 9)
                onInputChange({
                  target: { value: value, name: e.target.name },
                });
              if (!value)
                onInputChange({
                  target: { value: value, name: e.target.name },
                });
            }}
            invalid={!isEmpty(errors?.mobileNum?.message)}
            errorMessage={errors?.mobileNum?.message}
            inputProps={{
              textAlign: "left",
              value: values["mobileNum"],
            }}
            inputType={"input"}
            mobCode="+65"
            onBlurShowError
          />
          <InputGroup
            labelColor="#434343"
            id="emailId"
            name="emailId"
            label="Email"
            placeholder=""
            onChange={(e) => {
              if (e.target.value.length <= 50) onInputChange(e);
            }}
            invalid={!isEmpty(errors?.emailId?.message)}
            errorMessage={errors?.emailId?.message}
            inputProps={{
              textAlign: "left",
              value: values["emailId"],
            }}
            onBlurShowError
            inputType={"input"}
          />
        </ContactInputContainer>
        <FormActionContainer>
          <Button
            width="180px"
            variant="default"
            label="Submit"
            disabled={!formValid}
            onClick={onSubmit}
          />
        </FormActionContainer>
        <SubmitNote theme={theme}>
          By clicking &quot;Submit&quot;, you consent to Singapore Life Ltd (&quot;Singlife&quot;)
          collecting, using, disclosing or transferring your personal data to
          Singlife related companies, third party service providers or
          intermediaries (including your financial adviser, where applicable)
          whether located in Singapore or elsewhere, to assist you with your
          application. You can refer to Singlife&apos;s <Link
            text="Data Protection Notice"
            variant="link"
            target="_blank"
            rel="noopener noreferrer"
            url="https://singlife.com/en/pdpa#contact-us"
            color="#883E89"
            fontSize={16}
            fontSizeTablet={16}
            underline={true}
          /> for
          further details. If you wish to withdraw your consent, you may contact
          Singlife at{" "}
          <Link
            text="cs_life@singlife.com"
            variant="link"
            target="_blank"
            rel="noopener noreferrer"
            url="mailto:cs_life@singlife.com"
            color="#883E89"
            fontSize={16}
            fontSizeTablet={16}
            underline={true}
          />{" "}
          or{" "}
          <Link
            text=" +65 6827 9933"
            variant="link"
            url="tel:+6568279933"
            color="#883E89"
            fontSize={16}
            fontSizeTablet={16}
            underline={true}
          />
        </SubmitNote>
        <Contactus />
      </MainContainer>
    </>
  ) : (
    <SubmitSuccess />
  );
};
export default ContactForm;
